import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus,Loader2 } from "lucide-react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { showSuccessToast, showErrorToast } from "./toast/toast";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Checkbox } from "./ui/checkbox";

interface PalletType {
  id?: number;
  variant: string;
  name: string;
  numberOfPallets: number | string;
  shippingModes: number[];
  updatedAt?: string;
  createdAt?: string;
  projectId:number;
}

interface AddPalletDialogProps {
  onAddPallet: (palletData: PalletType) => Promise<void>;
  createPalletLoading?:boolean
  isOffline?: boolean;
}

const AddPalletDialog: React.FC<AddPalletDialogProps> = ({ onAddPallet,createPalletLoading,isOffline }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { shippingModes, projectId } = useSelector((state: RootState) => state.define);
  const { palletTypes } = useSelector((state: RootState) => state.pallets);

  const formik = useFormik({
    initialValues: {
      variant: "",
      name: "",
      shippingModes: [] as number[],
      numberOfPallets: '',
    },
    validationSchema: Yup.object({
      variant: Yup.string().required("Pallet Variant is required"),
      name: Yup.string().required("Pallet Name is required"),
      shippingModes: Yup.array().min(1, "At least one Shipping Mode is required"),
      numberOfPallets: Yup.number().required("Number of Pallets is required").min(1, "Must be at least 1"),
    }),
    onSubmit: async (values) => {
      const newPallet: any = {
        variant: values.variant,
        name: values.name,
        numberOfPallets: values.numberOfPallets,
        shippingModes: values.shippingModes,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        projectId: projectId,
        ...(isOffline && { id: Date.now() }),
        lpnNumbers: []
      };

      try {
      const res= await onAddPallet(newPallet);
      console.log(res,'gen result')
        setDialogOpen(false);
        formik.resetForm();
      } catch (error) {
        console.error("Error adding pallet:", error);
        showErrorToast("Failed to add pallet");
      }
    },
  });
console.log(shippingModes,'shippingModes')
  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        className={`${palletTypes?.length === 0 ? "animate-pulse" : ""} bg-light-blue w-full justify-start p-3 text-blue flex-center gap-1 font-bold hover:bg-light-blue text-md`}
      >
        <Plus className=" " size={20} /> Add
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[500px] p-6 z-[300]">
          <DialogHeader>
            <DialogTitle className="text-center mb-4">
              Add Pallet {isOffline ? '(Offline)' : ''}
            </DialogTitle>
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="label font-semibold">Pallet Variant</Label>
                <Select
                  name="variant"
                  onValueChange={(value) => formik.setFieldValue("variant", value)}
                  value={formik.values.variant}
                  // className="w-full"
                >
                  <SelectTrigger className=" z-[300]">
                    <SelectValue placeholder="Select Pallet Variant" />
                  </SelectTrigger>
                  <SelectContent className=" z-[300]">
                    {["Control 1", "Control 2", "Control 3", "Control 4", "Control 5", "Variable 1", "Variable 2", "Variable 3", "Variable 4", "Variable 5"].map((variant) => (
                      <SelectItem key={variant} value={variant}>
                        {variant}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formik.touched.variant && formik.errors.variant && (
                  <div className="text-red-500 text-sm">{formik.errors.variant}</div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="label font-semibold">Pallet Name</Label>
                <Input
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Pallet Name"
                  className="border border-border-style-1 rounded-sm p-2 w-full"
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="text-red-500 text-sm">{formik.errors.name}</div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="label font-semibold">Shipping Modes</Label>
                <div className="space-y-2">
                  {shippingModes.map((mode: any) => (
                    <div key={mode.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`mode-${mode.id}`}
                        checked={formik.values.shippingModes.includes(mode.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            formik.setFieldValue('shippingModes', [...formik.values.shippingModes, mode.id]);
                          } else {
                            formik.setFieldValue('shippingModes', formik.values.shippingModes.filter((id) => id !== mode.id));
                          }
                        }}
                      />
                      <label htmlFor={`mode-${mode.id}`} className="text-sm font-medium leading-none">
                        Mode {mode.mode}
                      </label>
                    </div>
                  ))}
                </div>
                {formik.touched.shippingModes && formik.errors.shippingModes && (
                  <div className="text-red-500 text-sm">{formik.errors.shippingModes}</div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="label font-semibold">Number of Pallets</Label>
                <Input
                  name="numberOfPallets"
                  type="number"
                  value={formik.values.numberOfPallets}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter Number of Pallets"
                  className="border border-border-style-1 rounded-sm p-2 w-full"
                />
                {formik.touched.numberOfPallets && formik.errors.numberOfPallets && (
                  <div className="text-red-500 text-sm">{formik.errors.numberOfPallets}</div>
                )}
              </div>

              <Button
                type="submit"
                disabled={createPalletLoading}
                className="w-full bg-blue hover:bg-[#00336C] hover:text-white rounded-md flex items-center gap-2  semi-bold-16 text-md font-semibold py-2"
              >
                {
                  createPalletLoading && <Loader2 className='animate-spin' />
                }
                Add {isOffline ? 'Offline' : ''} Pallet
              </Button>
            </form>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddPalletDialog;
